import { useEffect, useMemo, useState } from "react";
import { Flame } from "lucide-react";
import { http } from "@/lib/api";
import { logWarn } from "@/lib/log";
import { Celebrate } from "@/components/Mascot";

const WEEKS = 26;
const MILESTONES = [3, 7, 14, 30, 50, 100, 150, 200, 365];
const pad = (n) => String(n).padStart(2, "0");
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const shade = (m) => (m >= 60 ? "bg-sky-300" : m >= 30 ? "bg-sky-400/80" : m >= 10 ? "bg-sky-500/55" : m > 0 ? "bg-sky-500/30" : "bg-white/[0.05]");
const label = (m) => (m >= 60 ? `${Math.floor(m / 60)} h ${m % 60} min` : `${m} min`);
const plural = (n, w) => `${n} ${w}${n === 1 ? "" : "s"}`;

function useGrid() {
  return useMemo(() => {
    const today = new Date();
    const start = new Date(today);
    start.setDate(today.getDate() - today.getDay() - (WEEKS - 1) * 7);
    return Array.from({ length: WEEKS }, (_, w) => Array.from({ length: 7 }, (__, d) => {
      const x = new Date(start);
      x.setDate(start.getDate() + w * 7 + d);
      return x > today ? null : x;
    }));
  }, []);
}

// Celebrate a streak milestone once, on the day it's reached.
function StreakCelebration({ streak }) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (!streak?.active_today || !MILESTONES.includes(streak.current)) return;
    const key = `${streak.today}:${streak.current}`;
    if (localStorage.getItem("brynai.streakCelebrated") === key) return;
    localStorage.setItem("brynai.streakCelebrated", key);
    setShow(true);
  }, [streak]);
  if (!show) return null;
  return <div className="mb-4"><Celebrate testid="streak-celebrate" title={`${streak.current}-day streak!`} text="You've studied every day. Keep it going tomorrow." /></div>;
}

function StreakSummary({ streak }) {
  if (!streak) return null;
  let note = "Study today to start a streak";
  if (streak.current) note = streak.active_today ? "Studied today" : "Study today to keep it going";
  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-4" data-testid="streak-summary">
      <p className="flex items-center gap-2"><Flame className={`h-5 w-5 ${streak.current ? "text-orange-300" : "text-neutral-600"}`} />
        <span className="font-display text-2xl font-semibold" data-testid="streak-current">{streak.current}</span>
        <span className="text-sm text-neutral-400">{streak.current === 1 ? "day streak" : "days streak"}</span></p>
      <p className="text-xs text-neutral-500" data-testid="streak-longest">Longest: {plural(streak.longest, "day")}</p>
      <p className={`text-xs ${streak.active_today ? "text-emerald-300" : "text-neutral-500"}`} data-testid="streak-today">{note}</p>
    </div>
  );
}

export function ActivityHeatmap() {
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState(null);
  const grid = useGrid();
  useEffect(() => { http.get("/activity", { params: { days: WEEKS * 7 } }).then((r) => setData(r.data)).catch((e) => { logWarn("Activity failed", e); setData({ days: {}, study_days: [] }); }); }, []);
  const days = data?.days || {};
  const studied = useMemo(() => new Set(data?.study_days || []), [data]);
  const since = data?.tracking_since;
  const total = Object.values(days).reduce((a, b) => a + b, 0);
  const describe = (d) => {
    const k = iso(d);
    const when = d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
    if (days[k]) return `${when}: ${label(days[k])} of study time`;
    if (studied.has(k)) return `${when}: studied${!since || k < since ? " (time wasn't tracked yet)" : ""}`;
    return `${when}: no study`;
  };
  const cellCls = (k) => (days[k] ? shade(days[k]) : studied.has(k) ? "bg-sky-500/20 ring-1 ring-inset ring-sky-400/30" : "bg-white/[0.04]");
  return (
    <div className="soft-card p-5 sm:p-6" data-testid="activity-heatmap">
      <StreakCelebration streak={data?.streak} />
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
        <h3 className="font-display font-medium">Study activity</h3>
        <p className="text-xs text-neutral-500" data-testid="activity-total">{total ? `${label(total)} in the last ${WEEKS} weeks` : "Time you spend actively studying shows up here"}</p>
      </div>
      <StreakSummary streak={data?.streak} />
      <div className="overflow-x-auto scroll-thin pb-1">
        <div className="flex gap-[3px] w-max" role="grid" aria-label="Daily study time">
          {grid.map((week, w) => (
            <div key={w} className="flex flex-col gap-[3px]" role="row">
              {week.map((d, i) => d ? (
                <button key={i} type="button" title={describe(d)} aria-label={describe(d)} onClick={() => setPicked(d)} data-testid={`activity-day-${iso(d)}`}
                  className={`h-3 w-3 rounded-[3px] ${cellCls(iso(d))} outline-none focus-visible:ring-2 focus-visible:ring-sky-400`} />
              ) : <span key={i} className="h-3 w-3" />)}
            </div>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 mt-3 text-[11px] text-neutral-500">
        <p data-testid="activity-picked" aria-live="polite">{picked ? describe(picked) : "A day counts toward your streak when you chat, practise or study with BrynAI open."}</p>
        <span className="flex items-center gap-1">Less {[0, 5, 15, 40, 70].map((m) => <span key={m} className={`h-2.5 w-2.5 rounded-[2px] ${shade(m)}`} />)} More</span>
      </div>
    </div>
  );
}
